import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChatService,
  type ChatServiceConfig,
  type ChatServiceState,
} from '../services/chatService';

export interface UseChatOptions {
  config: ChatServiceConfig;
  autoInitialize?: boolean;
}

export interface UseChatResult extends ChatServiceState {
  initialize: () => Promise<void>;
  startConversation: (
    routingAttributes?: Record<string, string>
  ) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  endConversation: () => Promise<void>;
  clearError: () => void;
  isLoading: boolean;
  // User verification methods
  setIdentityToken: (identityToken: string) => Promise<void>;
  clearSession: () => Promise<void>;
}

/**
 * React hook for managing Salesforce chat functionality
 *
 * This hook provides a simple interface to the ChatService, handling
 * initialization, state management, and cleanup automatically.
 *
 * @param options - Configuration options for the chat
 * @returns Chat state and control functions
 */
export function useChat(options: UseChatOptions): UseChatResult {
  const { config, autoInitialize = true } = options;

  const chatServiceRef = useRef<ChatService | null>(null);
  const [state, setState] = useState<ChatServiceState>({
    conversation: null,
    messages: [],
    status: 'idle',
    isConnected: false,
    error: null,
    isUserVerified: false,
  });

  // Initialize ChatService
  useEffect(() => {
    chatServiceRef.current = ChatService.getInstance(config);

    // Subscribe to state changes
    const unsubscribe = chatServiceRef.current.subscribe(setState);

    return () => {
      unsubscribe();
    };
  }, [config]);

  // Auto-initialize if requested
  useEffect(() => {
    if (autoInitialize && chatServiceRef.current && state.status === 'idle') {
      chatServiceRef.current.initialize().catch((error) => {
        console.error('Auto-initialization failed:', error);
      });
    }
  }, [autoInitialize, state.status]);

  const initialize = useCallback(async (): Promise<void> => {
    if (!chatServiceRef.current) {
      throw new Error('ChatService not initialized');
    }
    await chatServiceRef.current.initialize();
  }, []);

  const startConversation = useCallback(
    async (routingAttributes?: Record<string, string>): Promise<void> => {
      if (!chatServiceRef.current) {
        throw new Error('ChatService not initialized');
      }
      await chatServiceRef.current.startConversation(routingAttributes);
    },
    []
  );

  const sendMessage = useCallback(async (content: string): Promise<void> => {
    if (!chatServiceRef.current) {
      throw new Error('ChatService not initialized');
    }
    await chatServiceRef.current.sendMessage(content);
  }, []);

  const endConversation = useCallback(async (): Promise<void> => {
    if (!chatServiceRef.current) {
      throw new Error('ChatService not initialized');
    }
    await chatServiceRef.current.endConversation();
  }, []);

  const clearError = useCallback((): void => {
    setState((prevState) => ({ ...prevState, error: null }));
  }, []);

  const setIdentityToken = useCallback(
    async (identityToken: string): Promise<void> => {
      if (!chatServiceRef.current) {
        throw new Error('ChatService not initialized');
      }
      await chatServiceRef.current.setIdentityToken(identityToken);
    },
    []
  );

  const clearSession = useCallback(async (): Promise<void> => {
    if (!chatServiceRef.current) {
      throw new Error('ChatService not initialized');
    }
    await chatServiceRef.current.clearSession();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chatServiceRef.current) {
        chatServiceRef.current.cleanup();
      }
    };
  }, []);

  const isLoading = state.status === 'connecting';

  return {
    ...state,
    initialize,
    startConversation,
    sendMessage,
    endConversation,
    clearError,
    isLoading,
    setIdentityToken,
    clearSession,
  };
}
